/**
 * Curated aliases: what a person types -> the exact USDA row they mean.
 *
 * USDA's filing style puts the head noun of a cut three segments deep
 * ("Chicken, broilers or fryers, breast, meat only, cooked, roasted"), which
 * no general ranking recovers from the phrase "chicken breast" without also
 * promoting breaded tenders and deli rolls. For the staples people log daily,
 * a short curated table is more honest than a cleverer heuristic: it says
 * exactly which row wins and why, and a test asserts every target exists.
 *
 * Only plain, cooked-as-eaten forms are mapped. Anything not in this table
 * falls through to the ordinary ranking, and the ranked alternatives are
 * always still offered.
 */
export const FOOD_ALIASES: Record<string, string> = {
  'egg': 'Eggs, Grade A, Large, egg whole',
  'eggs': 'Eggs, Grade A, Large, egg whole',
  'chicken breast': 'Chicken, broilers or fryers, breast, meat only, cooked, roasted',
  'chicken': 'Chicken, broilers or fryers, breast, meat only, cooked, roasted',
  'chicken thigh': 'Chicken, broilers or fryers, thigh, meat only, cooked, roasted',
  'turkey breast': 'Turkey, retail parts, breast, meat only, cooked, roasted',
  'turkey': 'Turkey, retail parts, breast, meat only, cooked, roasted',
  'salmon': 'Fish, salmon, Atlantic, farmed, cooked, dry heat',
  'tuna': 'Fish, tuna, light, canned in water, drained solids',
  'shrimp': 'Crustaceans, shrimp, cooked',
  'ground beef': 'Beef, ground, 85% lean meat / 15% fat, patty, cooked, broiled',
  'beef': 'Beef, ground, 85% lean meat / 15% fat, patty, cooked, broiled',
  'burger': 'Beef, ground, 85% lean meat / 15% fat, patty, cooked, broiled',
  'steak': 'Beef, loin, top sirloin cap steak, boneless, separable lean only, trimmed to 1/8" fat, choice, cooked, grilled',
  'sirloin': 'Beef, loin, top sirloin cap steak, boneless, separable lean only, trimmed to 1/8" fat, choice, cooked, grilled',
  'bacon': 'Pork, cured, bacon, cooked, baked',
  'white rice': 'Rice, white, long-grain, regular, enriched, cooked',
  'rice': 'Rice, white, long-grain, regular, enriched, cooked',
  'brown rice': 'Rice, brown, long-grain, cooked (Includes foods for USDA\'s Food Distribution Program)',
  'oats': 'Cereals, oats, regular and quick, not fortified, dry',
  'oatmeal': 'Cereals, oats, regular and quick, unenriched, cooked with water (includes boiling and microwaving), without salt',
  'quinoa': 'Quinoa, cooked',
  'pasta': 'Pasta, cooked, enriched, without added salt',
  'potato': 'Potatoes, baked, flesh and skin, without salt',
  'sweet potato': 'Sweet potato, cooked, baked in skin, flesh, without salt',
  'bread': 'Bread, whole-wheat, commercially prepared',
  'toast': 'Bread, whole-wheat, commercially prepared, toasted',
  'broccoli': 'Broccoli, cooked, boiled, drained, without salt',
  'spinach': 'Spinach, raw',
  'avocado': 'Avocados, raw, all commercial varieties',
  'apple': 'Apples, raw, with skin (Includes foods for USDA\'s Food Distribution Program)',
  'blueberries': 'Blueberries, raw',
  'banana': 'Bananas, raw',
  'almonds': 'Nuts, almonds',
  'peanut butter': 'Peanut butter, smooth style, with salt (Includes foods for USDA\'s Food Distribution Program)',
  'butter': 'Butter, salted',
  'milk': 'Milk, whole, 3.25% milkfat, with added vitamin D',
  'cottage cheese': 'Cheese, cottage, lowfat, 2% milkfat',
  'cheddar': 'Cheese, cheddar',
  'whey': 'Beverages, Whey protein powder isolate',
  'protein powder': 'Beverages, Whey protein powder isolate',
  'black beans': 'Beans, black, mature seeds, cooked, boiled, without salt',
  'lentils': 'Lentils, mature seeds, cooked, boiled, without salt',
  'olive oil': 'Oil, olive, salad or cooking',
  'coffee': 'Beverages, coffee, brewed, prepared with tap water',
  'black coffee': 'Beverages, coffee, brewed, prepared with tap water',
};

/** Normalise a typed phrase for alias lookup: lowercase, single spaces, singular-ish. */
export function aliasKey(query: string): string {
  const key = query.toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (FOOD_ALIASES[key]) return key;
  // "chicken breasts", "bananas": try the singular of the last word.
  const singular = key.replace(/(\w+?)(?:es|s)$/, '$1');
  if (FOOD_ALIASES[singular]) return singular;
  return key;
}
